import {
  CCol,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormSwitch,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import GradeService from 'src/service/GradeService'
import ToastComponent from 'src/components/common/TaostComponent'

const EditPointSetting = (props) => {
  const [loader, setLoader] = useState(false)
  // Both switches reflect grades.sr_active / econ_active - whether the comp uses SR / Econ
  // points at all. Off hides those rows from the point system table entirely, which is a
  // different thing from the per-row switches there (those only grey a row out).
  const [srEnabled, setSrEnabled] = useState(true)
  const [econEnabled, setEconEnabled] = useState(true)
  const [srEconLoading, setSrEconLoading] = useState(false)

  // gradeDetail arrives after this renders, so seed the switches when it lands.
  useEffect(() => {
    setSrEnabled(props.gradeDetail?.sr_active !== false)
    setEconEnabled(props.gradeDetail?.econ_active !== false)
  }, [props.gradeDetail])

  const showSr = srEnabled
  const showEcon = econEnabled

  const validationSchema = Yup.object().shape({
    ball_faced: showSr
      ? Yup.string().required('Ball faced is required').max(50, '50 Character Limit is allowed.')
      : Yup.string().nullable(),
    overs_bowled: showEcon
      ? Yup.string().required('Overs Bowled is required').max(50, '50 Character Limit is allowed.')
      : Yup.string().nullable(),
  })
  const formik = useFormik({
    initialValues: {
      ball_faced: props.gradeDetail?.ball_faced,
      overs_bowled: props.gradeDetail?.overs_bowled,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      data.gradeId = props.gradeDetail?.id
      actions.resetForm()
      setLoader(true)
      GradeService.updateGradePointSetting(data).then((res) => {
        if (res.status === 200) {
          props.toggleDetails(props.gradeId)
          props.fetchUsers()
          ToastComponent(res.message, 'success')
          setLoader(false)
        }
      })
    },
  })

  // Each switch flips just its own flag on the grade, which is what hides or shows those
  // rows in the point system table. They are independent, so any combination (one, both,
  // or neither) is valid.
  const handleToggle = (which, checked) => {
    const isSr = which === 'sr'
    const setEnabled = isSr ? setSrEnabled : setEconEnabled
    const previous = isSr ? srEnabled : econEnabled

    setEnabled(checked)
    setSrEconLoading(true)

    GradeService.toggleSrEcon({
      grade_id: props.gradeDetail?.id,
      type: isSr ? 'sr' : 'econ',
      is_active: checked ? 1 : 0,
    })
      .then((res) => {
        ToastComponent(res.message, 'success')
      })
      .catch((e) => {
        setEnabled(previous)
        ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
      })
      .finally(() => setSrEconLoading(false))
  }

  return (
    <>
      <h6 className="fw-bold mb-3">Setup Strike Rate & Economy</h6>
      <CRow className="mb-4">
        <CCol xs="auto">
          <CFormSwitch
            id="srEconTypeSr"
            label="Strike Rate"
            disabled={srEconLoading}
            checked={srEnabled}
            onChange={(e) => handleToggle('sr', e.target.checked)}
          />
        </CCol>
        <CCol xs="auto">
          <CFormSwitch
            id="srEconTypeEcon"
            label="Economy"
            disabled={srEconLoading}
            checked={econEnabled}
            onChange={(e) => handleToggle('econ', e.target.checked)}
          />
        </CCol>
      </CRow>
      <CForm className="row g-3" onSubmit={formik.handleSubmit}>
        {showSr && (
          <CCol md={6}>
            <CFormLabel className="fw-bold" htmlFor="grade">
              Use SR (Set minimum balls faced)
            </CFormLabel>
            <input
              type="text"
              name="ball_faced"
              className={
                'form-control' +
                (formik.errors.ball_faced && formik.touched.ball_faced ? ' is-invalid' : '')
              }
              id="validationServer01"
              placeholder="Use SR (Set minimum balls faced)"
              defaultValue={formik.values.ball_faced}
              onChange={formik.handleChange}
            />
            {formik.errors.ball_faced && formik.touched.ball_faced && (
              <CFormFeedback invalid>{formik.errors.ball_faced}</CFormFeedback>
            )}
          </CCol>
        )}
        {showEcon && (
          <CCol md={6}>
            <CFormLabel className="fw-bold" htmlFor="overs_bowled">
              Use Econ (Set minimum overs bowled)
            </CFormLabel>
            <input
              type="text"
              name="overs_bowled"
              className={
                'form-control' +
                (formik.errors.overs_bowled && formik.touched.overs_bowled ? ' is-invalid' : '')
              }
              id="validationServer01"
              placeholder="Use Econ (Set minimum overs bowled)"
              defaultValue={formik.values.overs_bowled}
              onChange={formik.handleChange}
            />
            {formik.errors.overs_bowled && formik.touched.overs_bowled && (
              <CFormFeedback invalid>{formik.errors.overs_bowled}</CFormFeedback>
            )}
          </CCol>
        )}
        <CCol md={6}>
          <CLoadingButton type="submit" color="success" variant="outline" loading={loader}>
            Submit
          </CLoadingButton>
        </CCol>
      </CForm>
    </>
  )
}

export default EditPointSetting
